import { NavLink, Outlet } from 'react-router';
import { PackageOpen } from 'lucide-react';
import { ADMIN_ROLES } from '@staff-app/shared-types';
import {
  SECTION_TAB_ACTIVE,
  SECTION_TAB_BASE,
  SECTION_TAB_IDLE,
  SECTION_TAB_LIST,
} from '@/lib/section-tabs';
import { useAuthStore } from '@/stores/auth-store';
import { useIntlRequestsAccess } from '@/features/intl-requests/hooks/useIntlRequests';

/**
 * DeviceOrdersShell — one sidebar entry, one page title, THREE sections.
 *
 * They are the same job at different stages: we issue a coupon code (Manage Coupon Codes), the
 * customer redeems it in the store, and the result is a Shopify order to chase (Search Shopify
 * Orders). They were briefly two top-level nav items, which both crowded the sidebar and hid the
 * fact that one leads to the other.
 *
 * International Requests joined as the third, to the RIGHT of the Shopify lookup (requester,
 * 2026-09-09). It is the same journey again at a stage the store cannot serve at all: a rep files
 * the order by hand because the customer is in a market the checkout does not ship to. It briefly
 * had its own sidebar entry and moved in here, which is the direction this shell was created for.
 *
 * Demo-device coupons were briefly a THIRD tab here and now live at /staff/devices-fulfillment
 * (requester, 2026-08-24). They mint the same kind of Shopify code, but with no invoice, no
 * payment and no chase they share none of the machinery these two sections are built around —
 * an exception wedged into a two-step flow rather than a step in it.
 *
 * The SHELL owns the h1 + icon (matching Trust Center's header), so each child renders an h2
 * subtitle instead of competing for the page title.
 *
 * ROUTING NOTE: the coupon queue is the INDEX route, so `/staff/order-devices` still renders it
 * directly with no redirect. Eight e2e specs navigate to that exact path and assert on
 * `order-devices-page`; a redirect would have worked but this keeps them entirely untouched.
 */
/**
 * THE TABS ARE ROLE-FILTERED, which they did not used to need to be.
 *
 * The first two sections are root/engineer/support; International Requests is ROOT ONLY
 * (requester, 2026-09-15). So the filter's remaining job is narrow but real: an `engineer` or a
 * `support` user must not be shown a third tab that would refuse them.
 *
 * ⚠️ IT ONCE INCLUDED `fulfillment`, AND THAT IS WHY THE FILTER IS NOT THE WHOLE STORY. A
 * fulfilment user was admitted to this shell for the sake of this one tab, but the sidebar links
 * to the BARE path, whose index is the coupon queue they cannot use — so they got "not
 * authorized" and never saw the tab bar this filter had carefully prepared for them. Filtering
 * tabs only helps somebody who can reach the shell; admitting a role to the shell needs a
 * DESTINATION for that role too. `demo-coupons-access.spec.ts` is what caught it.
 *
 * Filtering here rather than letting every tab render is still the point: a tab bar whose links
 * refuse you is worse than a missing tab, because it reads as a broken permission rather than as
 * a section you do not own. The route guards in `routes/index.tsx` are the enforcement — this is
 * the UX half, and the two lists have to be kept in agreement by hand.
 */
const TABS = [
  {
    to: '.',
    end: true,
    label: 'Manage Coupon Codes',
    testId: 'tab-manage-coupons',
    roles: [ADMIN_ROLES.ROOT, ADMIN_ROLES.ENGINEER, ADMIN_ROLES.SUPPORT] as string[],
  },
  {
    to: 'shopify',
    end: false,
    label: 'Search Shopify Orders',
    testId: 'tab-shopify-orders',
    roles: [ADMIN_ROLES.ROOT, ADMIN_ROLES.ENGINEER, ADMIN_ROLES.SUPPORT] as string[],
  },
  {
    to: 'international',
    end: false,
    label: 'International Requests',
    testId: 'tab-international-requests',
    // ROOT + SUPPORT + FULFILLMENT since 2026-09-21. `intl.visible` below is still consulted, so
    // a role listed here whom the SERVER refuses is shown nothing — the rollout gate stays the
    // authority and this list only decides who is a candidate for the tab.
    roles: [ADMIN_ROLES.ROOT, ADMIN_ROLES.SUPPORT, ADMIN_ROLES.FULFILLMENT] as string[],
  },
  {
    // All-International Requests — several devices per request, routed across Amazon MCF, China
    // and Houston (Paul, 2026-10-26). To the RIGHT of International Requests, which it does not
    // replace. Same role tier and the same rollout gate (`INTL_REQUESTS_ROOT_ONLY`): the AIR
    // resolver mounts the same IntlRolloutGuard, so one answer covers both tabs.
    to: 'all-international',
    end: false,
    label: 'All International',
    testId: 'tab-all-international',
    roles: [ADMIN_ROLES.ROOT, ADMIN_ROLES.SUPPORT, ADMIN_ROLES.FULFILLMENT] as string[],
  },
];

/** The tabs behind the international rollout gate. */
const GATED = new Set(['international', 'all-international']);

export function DeviceOrdersShell() {
  const roles = useAuthStore((s) => s.adminUser?.roles ?? []);
  /**
   * THE ROLLOUT GATE, on top of the role tiers (requester, 2026-09-10).
   *
   * Kept even though the role tier is now root-only too (requester, 2026-09-15), because the two
   * axes answer different questions and the flag is the one that can change without a deploy:
   * `roles` is who may ever use it, `intl.visible` is whether the environment has it open. With
   * both root-only they agree, and the gate is the belt to the tier's braces.
   *
   * The server is the authority (`IntlRolloutGuard`); this only stops a support user clicking
   * into a page that answers 403. The answer rides `staffMe` (Paul, 2026-10-29), so the tabs
   * draw with the page; an answer that is absent reads as NOT visible.
   */
  const intl = useIntlRequestsAccess();
  const tabs = TABS.filter(
    (t) =>
      t.roles.some((r) => (roles as string[]).includes(r)) &&
      (!GATED.has(t.to) || intl.visible),
  );

  return (
    <div className="flex h-full flex-col overflow-hidden" data-testid="device-orders-shell">
      <div className="px-6 pt-6">
        <h1 className="flex items-center gap-2 text-2xl font-bold text-foreground">
          <PackageOpen className="h-6 w-6" />
          {'Devices Order'}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {'Hardware device orders end to end — issue the coupon code, track the Shopify order '}
          {'it becomes, and file the ones that ship somewhere the store does not serve.'}
        </p>
      </div>

      {/* A sub-nav, deliberately NOT the Tabs component: the coupon queue has its own tab row
          (Active / All / Unpaid coupons), and two identical-looking tab bars stacked on top of
          each other read as one confused control rather than a hierarchy. */}
      <nav className={`mt-4 ${SECTION_TAB_LIST}`} aria-label="Device orders sections">
        {tabs.map(({ to, end, label, testId }) => (
          <NavLink
            key={label}
            to={to}
            end={end}
            data-testid={testId}
            className={({ isActive }) =>
              [SECTION_TAB_BASE, isActive ? SECTION_TAB_ACTIVE : SECTION_TAB_IDLE].join(' ')
            }
          >
            {label}
          </NavLink>
        ))}
      </nav>

      <div className="min-h-0 flex-1 overflow-auto">
        <Outlet />
      </div>
    </div>
  );
}
